import { useEffect, useRef, useState, type ReactNode } from 'react';
import { CalendarDotsIcon } from '@phosphor-icons/react';
import { Popover, PopoverContent } from '@voll-intelligence/ui-kit/ui/popover';
import { TravelCalendar, type DateRange } from '../../shared/travel_calendar.js';
import { TravelCalendarConfirm } from '../../shared/travel_calendar_confirm.js';
import { PageFieldSheet } from '../../shared/page_field_sheet.js';
import { returnFocusUnlessMoved } from '../../shared/return_focus.js';
import { SearchField } from '../../shared/search_field.js';
import { useGuidedOpen, type FieldGuide } from '../../shared/guided_flow.js';

/*
 * O QUANDO do ônibus: a ida e, se houver, a volta no mesmo calendário do carro
 * e do hotel — dois meses no popover, a folha de tela cheia no palco estreito.
 * A diferença é a volta OPCIONAL (#2440): o Confirmar acende já com a ida, e
 * confirmar só ela é a busca só de ida. Volta no mesmo dia vale
 * (`minNights: 0`).
 *
 * O rascunho vive SÓ enquanto o painel está em cena: abrir, mexer e desistir
 * devolve as datas que a busca realmente tem — a mesma régua do carro.
 *
 * Com a aba "Somente ida" (`oneWay`, a busca da conversa) o calendário escolhe
 * um dia só e o toque já confirma — como o QUANDO do aéreo na mesma aba; com
 * "Ida e volta" (`needsReturn`) o Confirmar espera a volta.
 */

export type BusWhenCopy = {
  calendarLocale: string;
  previousMonthLabel: string;
  nextMonthLabel: string;
  /** A PERGUNTA da coluna e o título da folha ("Quando"). */
  whenLabel: string;
  /** A pílula sem data ("A definir"). */
  whenPlaceholder: string;
  /** A pílula com a ida e, se houver, a volta ("25 ago – 27 ago"; só ida,
   *  "Seg, 25 ago"). Só é chamada com a ida. */
  whenSummary(range: DateRange): string;
  /** O resumo do rascunho no pé do calendário ("Seg, 25 ago → escolha a volta"). */
  whenDraftSummary(range: DateRange): string;
  /** O Confirmar do popover — nomeia as datas escolhidas. */
  confirmWhenLabel(range: DateRange): string;
  /** O Confirmar FIXO da folha estreita ("Confirmar datas"). */
  confirmWhenSheetLabel: string;
  /** O Confirmar quando ele também BUSCA ("Confirmar e buscar"), nos dois
   *  palcos: as datas já estão no resumo logo acima. */
  confirmAndSearchLabel: string;
};

type Layout = 'popover' | 'sheet';

function WhenDraft({
  initial,
  copy,
  layout,
  minDate,
  searches,
  oneWay,
  needsReturn,
  onConfirm,
}: {
  readonly initial: DateRange;
  readonly copy: BusWhenCopy;
  readonly layout: Layout;
  readonly minDate: string;
  readonly searches: boolean;
  readonly oneWay: boolean;
  readonly needsReturn: boolean;
  readonly onConfirm: (range: DateRange) => void;
}): ReactNode {
  const [draft, setDraft] = useState<DateRange>(initial);
  const sheetLabel = searches ? copy.confirmAndSearchLabel : copy.confirmWhenSheetLabel;
  if (oneWay) {
    return (
      <TravelCalendar
        mode="single"
        value={initial.from ? { from: initial.from } : {}}
        locale={copy.calendarLocale}
        previousMonthLabel={copy.previousMonthLabel}
        nextMonthLabel={copy.nextMonthLabel}
        minDate={minDate}
        months={2}
        layout={layout}
        onChange={(range) => {
          if (range.from) onConfirm({ from: range.from });
        }}
      />
    );
  }
  return (
    <TravelCalendar
      value={draft}
      locale={copy.calendarLocale}
      previousMonthLabel={copy.previousMonthLabel}
      nextMonthLabel={copy.nextMonthLabel}
      minNights={0}
      minDate={minDate}
      months={2}
      layout={layout}
      onChange={setDraft}
      footer={
        <TravelCalendarConfirm
          summary={copy.whenDraftSummary(draft)}
          confirmLabel={layout === 'sheet' || searches ? sheetLabel : copy.confirmWhenLabel(draft)}
          disabled={!draft.from || (needsReturn && !draft.to)}
          full={layout === 'sheet'}
          onConfirm={() => {
            onConfirm(draft);
          }}
        />
      }
    />
  );
}

export function BusWhenField({
  value,
  copy,
  minDate,
  disabled,
  sheet,
  guide,
  searches = false,
  oneWay = false,
  needsReturn = false,
  onConfirm,
}: {
  readonly value: DateRange;
  readonly copy: BusWhenCopy;
  /** O primeiro dia escolhível (hoje, no fuso da pessoa). */
  readonly minDate: string;
  readonly disabled: boolean;
  /** FOLHA de tela cheia em vez do popover (o palco estreito). */
  readonly sheet?: { readonly backLabel: string } | undefined;
  /** Ver `useGuidedFlow`. Ausente ⇒ sem anel e sem abertura por conta do guia. */
  readonly guide?: FieldGuide;
  /** O Confirmar também busca (ver `GuidedFlow.confirmSearches`): o rótulo diz. */
  readonly searches?: boolean;
  /** Um dia só, confirmado no toque (a aba "Somente ida"). */
  readonly oneWay?: boolean;
  /** A volta é obrigatória (a aba "Ida e volta"): o Confirmar espera as duas. */
  readonly needsReturn?: boolean;
  readonly onConfirm: (range: DateRange) => void;
}): ReactNode {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useGuidedOpen(guide);
  /* `busy` do host FECHA o painel: um Confirmar clicável durante a busca
     mudaria o pedido pelas costas dela. */
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled, setOpen]);
  const trigger = (
    <SearchField
      triggerRef={triggerRef}
      icon={CalendarDotsIcon}
      label={copy.whenLabel}
      text={value.from ? copy.whenSummary(value) : copy.whenPlaceholder}
      muted={!value.from}
      naked
      wanted={guide?.wanted === true}
      expanded={open}
      disabled={disabled}
      onOpen={() => {
        setOpen(!open);
      }}
    />
  );
  const calendar = (layout: Layout) => (
    <WhenDraft
      initial={value}
      copy={copy}
      layout={layout}
      minDate={minDate}
      searches={searches}
      oneWay={oneWay}
      needsReturn={needsReturn}
      onConfirm={(range) => {
        setOpen(false);
        // O foco volta à pílula ANTES de a barra saber das datas: no
        // preenchimento guiado é ela quem, com tudo respondido, o leva ao Buscar.
        triggerRef.current?.focus();
        onConfirm(range);
      }}
    />
  );
  if (sheet) {
    return (
      <>
        {trigger}
        <PageFieldSheet
          open={open}
          onClose={() => {
            setOpen(false);
          }}
          // À pílula — salvo se o foco já pousou noutro lugar (o Buscar, no
          // preenchimento guiado).
          returnFocusTo={() => returnFocusUnlessMoved(triggerRef)()}
          title={copy.whenLabel}
          backLabel={sheet.backLabel}
        >
          {calendar('sheet')}
        </PageFieldSheet>
      </>
    );
  }
  return (
    <>
      {trigger}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverContent
          anchor={triggerRef}
          align="start"
          aria-label={copy.whenLabel}
          className="w-auto gap-2 rounded-xl p-2"
        >
          {calendar('popover')}
        </PopoverContent>
      </Popover>
    </>
  );
}
